// The SAVON API as the staff app uses it. Every call sends the staff access
// token; a 401 signs the person out.

export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "https://api.amarmeds.tech/api/v1";

const TOKEN_KEY = "savon-staff-token";

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function setToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // Private mode: the person signs in again next time.
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly body: Record<string, unknown>,
  ) {
    super(code);
  }
}

let onSignedOut: () => void = () => {};
export const whenSignedOut = (f: () => void) => {
  onSignedOut = f;
};

export async function api<T>(path: string, init: { method?: string; body?: unknown; token?: string } = {}): Promise<T> {
  const token = init.token ?? getToken();
  const res = await fetch(API_URL + path, {
    method: init.method ?? (init.body === undefined ? "GET" : "POST"),
    headers: {
      ...(init.body === undefined ? {} : { "content-type": "application/json" }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok) {
    if (res.status === 401 && !init.token) {
      setToken(null);
      onSignedOut();
    }
    throw new ApiError(res.status, String(json.error ?? res.status), json);
  }
  return json as T;
}

/** A file the API sends (the prescription photo), as a Blob; null when there is none (404). */
export async function apiBlob(path: string): Promise<Blob | null> {
  const token = getToken();
  const res = await fetch(API_URL + path, { headers: token ? { authorization: `Bearer ${token}` } : {}, cache: "no-store" });
  if (res.status === 404) return null;
  if (!res.ok) {
    if (res.status === 401) {
      setToken(null);
      onSignedOut();
    }
    throw new ApiError(res.status, String(res.status), {});
  }
  return res.blob();
}

export type Me = {
  staffId: number;
  name: string;
  role: "pharmacy" | "rider" | "ops";
  pharmacyId: number | null;
  pharmacy: { id: number; name: string; open: boolean; zoneName: string } | null;
};

export type MedicineName = { brandName: string; genericName?: string; strength: string; dosageForm: string } | null;

export type QueueLine = {
  lineId: number;
  medicineId: number;
  medicine: MedicineName;
  strips: number;
  unitPricePaisa: number;
  lineStatus: string;
  substitute: { medicineId: number; medicine: MedicineName; unitPricePaisa: number | null } | null;
  linePaisa: number;
};

export type QueueOrder = {
  orderId: string;
  status: string;
  reason: string | null;
  zone: string;
  area: string | null;
  deliverBy: string | null;
  placedAt: string;
  minutesWaiting: number;
  /** When the pharmacy should have accepted a new order (null once it is past "placed"). */
  acceptBy: string | null;
  deliveredAt: string | null;
  paymentMethod: string;
  hasPrescription: boolean;
  /** Address and phone, once the pharmacy has confirmed the order. */
  contact: { address: string; phone: string } | null;
  lines: QueueLine[];
  subtotalPaisa: number;
  totalPaisa: number;
};

export type OpsOrder = {
  orderId: string;
  status: string;
  reason: string | null;
  pharmacyId: number;
  zone: string;
  runId: number | null;
  paymentMethod: string;
  totalPaisa: number;
  minutesSincePlaced: number;
  deliveryCodeTries: number;
  address: string | null;
  phone: string | null;
};

export type OpsOrderDetail = {
  orderId: string;
  status: string;
  reason: string | null;
  pharmacy: { id: number; name: string };
  zone: string;
  area: string | null;
  deliverBy: string | null;
  placedAt: string;
  deliveredAt: string | null;
  paymentMethod: string;
  deliveryCodeTries: number;
  run: { runId: number; status: string; riderId: number; riderName: string } | null;
  contact: { address: string; phone: string } | null;
  hasPrescription: boolean;
  canCancel: boolean;
  canEditContact: boolean;
  canEditLines: boolean;
  lines: QueueLine[];
  subtotalPaisa: number;
  deliveryFeePaisa: number;
  totalPaisa: number;
  payment: { paidPaisa: number; refundPaisa: number; collectOnDeliveryPaisa: number; cashCollectedPaisa: number | null };
  events: { from: string | null; to: string; actor: string; note: string | null; at: string }[];
};

export type RunHistory = {
  runId: number;
  status: string;
  rider: { id: number; name: string };
  pharmacy: { id: number; name: string };
  createdAt: string;
  pickedUpAt: string | null;
  doneAt: string | null;
  delivered: number;
  failed: number;
  cashCollectedPaisa: number;
  orders: { orderId: string; status: string; totalPaisa: number; cashCollectedPaisa: number | null; deliveredAt: string | null; reason: string | null }[];
};

export type ReportTally = {
  placed: number;
  delivered: number;
  rejected: number;
  cancelled: number;
  open: number;
  deliveredPaisa: number;
  feesPaisa: number;
  cashPaisa: number;
  lateAnswers: number;
  failedDeliveries: number;
  medianAnswerMinutes: number | null;
};

export type Report = {
  from: string;
  to: string;
  totals: ReportTally & { depositsPaisa: number; deposits: number };
  rejectReasons: Record<string, number>;
  cancelReasons: Record<string, number>;
  byPharmacy: (ReportTally & { pharmacyId: number; name: string })[];
  byRider: (ReportTally & { riderId: number; name: string })[];
  byDay: (ReportTally & { day: string })[];
};

export type OpsZone = {
  key: string;
  name: string;
  active: boolean;
  deliveryFeePaisa: number;
  areas: string[];
  waves: { cutoff: string; deliverBy: string }[];
};

export type OpsPharmacy = {
  id: number;
  name: string;
  zoneKey: string;
  zoneName: string;
  opensAt: string;
  closesAt: string;
  licenceNo: string;
  active: boolean;
  priceCount: number | null;
};

export type OpsPrice = {
  medicineId: number;
  brandName: string;
  genericName: string;
  strength: string;
  dosageForm: string;
  stripPricePaisa: number | null;
  inStock: boolean;
};

export type OpsStaff = {
  id: number;
  name: string;
  role: "pharmacy" | "rider" | "ops";
  pharmacyId: number | null;
  phone: string;
  active: boolean;
  cashLimitPaisa: number;
  createdAt: string;
};

export type Rider = {
  riderId: number;
  name: string;
  phone: string;
  active: boolean;
  cashHeldPaisa: number;
  cashLimitPaisa: number;
  activeRun: { runId: number; status: string } | null;
};

export type RiderStop = {
  orderId: string;
  status: string;
  items: number;
  zone: string;
  area: string | null;
  deliverBy: string | null;
  collectPaisa: number;
  address: string | null;
  phone: string | null;
  codeLocked: boolean;
};

export type RiderRun = { runId: number; status: string; pharmacy: { name: string } | null; stops: RiderStop[] } | null;

/** Everything on the rider's screen, from GET /rider/run. */
export type RiderScreen = {
  run: RiderRun;
  cash: { heldPaisa: number; limitPaisa: number };
  /** Bags that left a run after pickup and must go back to the pharmacy. */
  returns: { orderId: string; runId: number; why: "delivery_failed" | "cancelled"; at: string }[];
  opsPhone: string | null;
  lastRun: { runId: number; doneAt: string | null; delivered: number; failed: number; cancelled: number; cashCollectedPaisa: number } | null;
};

/** Shared by the rider screen and the new-run alert, so both read one request. */
export const riderScreenQuery = { queryKey: ["run"], queryFn: () => api<RiderScreen>("/rider/run") } as const;

export type MedicineHit = {
  medicineId: number;
  brandName: string;
  genericName: string;
  strength: string;
  dosageForm: string;
  stripPricePaisa: number | null;
  inStock: boolean | null;
};

export type SubstituteOption = {
  medicineId: number;
  brandName: string;
  genericName: string;
  strength: string;
  dosageForm: string;
  manufacturer: string;
  stripPricePaisa: number | null;
  inStock: boolean;
};
